import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from "firebase/auth";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";

import { UserContext } from "../../context/UserContext";
import { db } from "../../services/firebaseConfig";
import css from "./Account.module.css";

export const AccountPage = () => {
  const { t } = useTranslation("user");
  const { user, setUser, userData, setUserData } = useContext(UserContext);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [status, setStatus] = useState("");
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // New: confirmation modal for account deletion
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  useEffect(() => {
    setDisplayName(userData?.displayName || "");
    setEmail(userData?.email || "");
  }, [userData]);

  if (!user) return <p>{t("account.loading")}</p>;

  async function handleUpdate(e) {
    e.preventDefault();
    setStatus("");

    const emailChanged = email !== user.email;
    const passwordChanged = password.trim() !== "";

    // Se for necessário reautenticar, abre o modal e pausa a ação
    if ((emailChanged || passwordChanged) && !showReauthModal) {
      setShowReauthModal(true);
      return;
    }

    setLoading(true);
    try {
      // Atualiza displayName
      if (displayName !== user.displayName) {
        await updateProfile(user, { displayName });
      }

      // Atualiza email
      if (emailChanged) {
        await updateEmail(user, email);
      }

      // Atualiza senha
      if (passwordChanged) {
        await updatePassword(user, password);
      }

      // Atualiza Firestore (userData)
      const userDocRef = doc(db, "userData", user.uid);
      await updateDoc(userDocRef, {
        displayName,
        email,
        updatedAt: new Date(),
      });

      setUser({ ...user, displayName, email });
      setUserData({ ...userData, displayName, email });

      setStatus(t("account.updatedInfo"));
      setPassword("");
      setCurrentPassword("");
      setShowReauthModal(false);
    } catch (error) {
      console.error("Erro:", error);
      let message = error.message;
      if (error.code === "auth/wrong-password")
        message = t("account.wrongPass1");
      else if (error.code === "auth/requires-recent-login")
        message = t("account.errorLogin");
      setStatus(`Erro: ${message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleReauthenticate(e) {
    e.preventDefault();
    setStatus(t("account.checkingCredencials"));
    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      setStatus(t("account.reauthenticate"));
      setShowReauthModal(false);
      // continue the update flow
      const fakeEvent = { preventDefault: () => {} };
      await handleUpdate(fakeEvent);
    } catch (error) {
      console.error(t("account.reauthenticateError"), error);
      setStatus(t("account.wrongPass2"));
    }
  }

  // Apaga somente os dados de personalidade (LGPD-friendly)
  async function handleDeletePersonality() {
    if (!window.confirm(t("account.deleteData"))) return;

    setLoading(true);
    setStatus(t("account.deleting"));
    try {
      const userDocRef = doc(db, "userData", user.uid);
      const patch = {
        personalityTraits: null,
        tools: {},
        updatedAt: new Date(),
      };
      await updateDoc(userDocRef, patch);

      // atualiza contexto local
      setUserData((prev) => ({ ...(prev || {}), ...patch }));

      setStatus(t("account.personalityDeleted"));
    } catch (err) {
      console.error(t("account.personalityDeletedError"), err);
      setStatus(t("account.deletingError"));
    } finally {
      setLoading(false);
    }
  }

  // Excluir conta: tenta apagar Firestore e depois o usuário do Auth (requer login recente)
  async function handleDeleteAccount() {
    setLoading(true);
    setStatus(t("account.deletingStart"));

    try {
      const userDocRef = doc(db, "userData", user.uid);

      // 0) Auditoria antes de apagar
      try {
        await updateDoc(userDocRef, {
          deletionRequestedAt: new Date().toISOString(),
          deletionRequestedBy: user.uid,
        });
      } catch (auditErr) {
        console.warn(
          "Falha ao registrar auditoria, prosseguindo mesmo assim:",
          auditErr
        );
        // Mesmo se falhar, não devemos impedir a exclusão da conta
      }

      // 1) Apaga documento Firestore (userData)
      try {
        await deleteDoc(userDocRef);
      } catch (fireErr) {
        console.warn(
          "Não foi possível apagar doc diretamente, marcando deletedAt:",
          fireErr
        );
        // Se não conseguiu apagar, ao menos marcar
        await updateDoc(userDocRef, {
          deletedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }

      // 2) Tenta apagar o usuário do Firebase Auth
      try {
        await deleteUser(user);
        setUser(null);
        setUserData(null);
        setStatus(t("account.deletedSuccess"));
      } catch (authErr) {
        console.error("Erro ao apagar usuário do Auth:", authErr);

        if (authErr.code === "auth/requires-recent-login") {
          setStatus(t("account.confirmPass"));
          setShowReauthModal(true);
        } else {
          setStatus(t("account.deleteLoginError"));
        }
      }
    } catch (err) {
      console.error("Erro no processo de exclusão:", err);
      setStatus(t("account.deleteAccountError"));
    } finally {
      setLoading(false);
    }
  }

  if (!userData) return <p>{t("account.loadingUserInfo")}</p>;

  return (
    <div className={css.accountContainer}>
      <h2 className={css.accountTitle}>{t("account.title")}</h2>

      <form onSubmit={handleUpdate} className="accountForm">
        <div className={css.formField}>
          <label>{t("account.userName")}</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        <div className={css.formField}>
          <label>{t("account.userEmail")}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className={css.formField}>
          <label>{t("account.newPass")}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("account.passPlaceholder")}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginTop: 12,
          }}
        >
          <button type="submit" className={css.btnPrimary} disabled={loading}>
            {loading ? t("account.saving") : t("account.saveChanges")}
          </button>

          <button
            type="button"
            className={css.btnSecondary}
            onClick={() => {
              // redirecionar para página de perfil (opcional)
            }}
            disabled={loading}
          >
            {t("account.cancelButton")}
          </button>
        </div>
      </form>

      {status && <p className={css.statusMessage}>{status}</p>}

      {/* Reauth modal (usado também no fluxo de exclusão se reauth necessário) */}
      {showReauthModal && (
        <div className={css.modalOverlay}>
          <div className={css.modal}>
            <h3>{t("acount.confirmPassTitle")}</h3>
            <p>{t("acount.confirmPassText")}</p>

            <form onSubmit={handleReauthenticate}>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Senha atual"
                required
              />
              <div className={css.modalButtons}>
                <button type="submit" className={css.btnPrimary}>
                  {t("acount.confirmButton")}
                </button>
                <button
                  type="button"
                  className={css.btnSecondary}
                  onClick={() => {
                    setShowReauthModal(false);
                    setCurrentPassword("");
                  }}
                >
                  {t("acount.cancelButton")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Privacy / account actions */}
      <div style={{ marginTop: 24 }}>
        <div className={css.card}>
          <h3>{t("account.privacyTitle")}</h3>
          <p className={css.muted}>{t("account.privacySubtitle")}</p>
          <div style={{ marginTop: 8 }}>
            <button
              className={css.dangerBtn}
              onClick={handleDeletePersonality}
              disabled={loading}
            >
              {t("account.deleteButton")}
            </button>
          </div>
        </div>

        <div className={css.card} style={{ marginTop: 12 }}>
          <h3>{t("account.deleteTitle")}</h3>
          <p className={css.muted}>{t("account.deleteSubtitle")}</p>
          <div style={{ marginTop: 8 }}>
            {/* Open confirmation modal first */}
            <button
              className={css.dangerBtnOutline}
              onClick={() => setShowDeleteConfirmModal(true)}
              disabled={loading}
            >
              {t("account.deleteButton2")}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation modal before account deletion */}
      {showDeleteConfirmModal && (
        <div className={css.modalOverlay}>
          <div className={css.modal}>
            <h3>{t("account.modalTitle")}</h3>
            <p>{t("account.modalText")}</p>

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <button
                className={css.btnSecondary}
                onClick={() => setShowDeleteConfirmModal(false)}
                disabled={loading}
              >
                {t("account.cancelButton")}
              </button>
              <button
                className={css.dangerBtn}
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  // start deletion flow
                  handleDeleteAccount();
                }}
                disabled={loading}
              >
                {t("account.modalConfirmButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
